// Authentication Services
// Firebase authentication operations

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  updateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  UserCredential,
  User
} from 'firebase/auth';
import { auth } from '@/shared/lib/firebase';

import ServiceBase from '@/shared/api/base/ServiceBase';
import { AuthError } from '@/shared/api/errors/ApiError';

class AuthService extends ServiceBase {
  constructor() {
    super('AuthService');
  }

  // Login with email and password
  async login(email: string, password: string): Promise<User> {
    try {
      this.validate.validateEmail(email);
      this.validate.validatePassword(password);

      const userCredential: UserCredential = await signInWithEmailAndPassword(auth, email, password);
      this.log(`User ${userCredential.user.uid} logged in`);
      return userCredential.user;
    } catch (error) {
      this.logError(error, { method: 'login', email });
      throw error;
    }
  }

  // Register new user
  async register(email: string, password: string): Promise<User> {
    try {
      this.validate.validateEmail(email);
      this.validate.validatePassword(password);

      const userCredential: UserCredential = await createUserWithEmailAndPassword(auth, email, password);
      this.log(`User ${userCredential.user.uid} registered`);
      return userCredential.user;
    } catch (error) {
      this.logError(error, { method: 'register', email });
      throw error;
    }
  }

  // Logout current user
  async logout(): Promise<void> {
    try {
      const uid = auth.currentUser?.uid;
      await signOut(auth);
      if (uid) {
        this.log(`User ${uid} logged out`);
      }
    } catch (error) {
      this.logError(error, { method: 'logout' });
      throw error;
    }
  }

  // Send password reset email
  async resetPassword(email: string): Promise<void> {
    try {
      this.validate.validateEmail(email);
      await sendPasswordResetEmail(auth, email);
      this.log(`Password reset email sent to ${email}`);
    } catch (error) {
      this.logError(error, { method: 'resetPassword', email });
      throw error;
    }
  }

  // Change user password (requires recent authentication)
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      this.validate.validatePassword(currentPassword);
      this.validate.validatePassword(newPassword);

      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new AuthError('No user logged in');
      }

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, newPassword);
      this.log(`User ${user.uid} changed password`);
    } catch (error) {
      this.logError(error, { method: 'changePassword' });
      throw error;
    }
  }

  // Change user email (requires recent authentication)
  async changeEmail(currentPassword: string, newEmail: string): Promise<void> {
    try {
      this.validate.validatePassword(currentPassword);
      this.validate.validateEmail(newEmail);

      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new AuthError('No user logged in');
      }

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      await updateEmail(user, newEmail);
      this.log(`User ${user.uid} changed email to ${newEmail}`);
    } catch (error) {
      this.logError(error, { method: 'changeEmail' });
      throw error;
    }
  }

  // Get current user
  // @ts-expect-error - Overriding base method with wider return type (User | null)
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!auth.currentUser;
  }
}

const authServices = new AuthService();
export default authServices;

// Named exports for backward compatibility
export const login = (email: string, password: string) => authServices.login(email, password);
export const register = (email: string, password: string) => authServices.register(email, password);
export const logout = () => authServices.logout();
export const resetPassword = (email: string) => authServices.resetPassword(email);
export const changePassword = (current: string, next: string) => authServices.changePassword(current, next);
export const changeEmail = (password: string, email: string) => authServices.changeEmail(password, email);
export const getCurrentUser = () => authServices.getCurrentUser();
export const isAuthenticated = () => authServices.isAuthenticated();
